import type { ReadonlyDeep } from 'type-fest';
import type { InitialData as DispatchJobSearchFormInitialData } from '@/components/searchForms/DispatchJobSearchForm.vue';
import type { DataItem as DispatchJobsTableDataItem } from '@/components/tables/DispatchJobsTable.vue';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { TZDate, tz } from '@date-fns/tz';
import { isSameDay } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import _cloneDeep from 'lodash/cloneDeep';

import * as dateHelpers from '@/helpers/date';
import * as oneTrackingJobHelpers from '@/helpers/trackingJob/oneTrackingJob';
import * as commonConstants from '@/constants/common';
import * as customerConstants from '@/constants/customer';
import { dispatchJobsTabs } from '@/constants/tabs';

const timeZone = 'Australia/Sydney';

function getTime(date: string | undefined, timeZone: string): string {
  if (!date) return commonConstants.EM_DASH;
  try {
    // return format(toZonedTime(date, timeZone), 'hh:mm a, MMM d'); // it gives the same result
    return formatInTimeZone(date, timeZone, 'hh:mm a, MMM d');
  } catch (err) {
    return commonConstants.EM_DASH;
  }
}

export function getTableItem(
  dispatchJob: ReadonlyDeep<openapi.DespatchJobsContentOpenapi[0]>,
  dispatchJobsLocks: ReadonlyDeep<Record<number, boolean>>,
): DispatchJobsTableDataItem {
  const { jobId, jobDate } =
    oneTrackingJobHelpers.getJobIdAndJobDate(dispatchJob);
  const mainStopNum = oneTrackingJobHelpers.getMainStopNum(dispatchJob);
  const mainStop = dispatchJob.stops.at(mainStopNum - 1);
  const timeTooltip = mainStop?.required_at;
  let isLocked = dispatchJob.is_locked;
  if (dispatchJob.id in dispatchJobsLocks) {
    isLocked = dispatchJobsLocks[dispatchJob.id];
  }

  return {
    id: dispatchJob.id,
    isLocked,
    jobId,
    jobDate,
    time: getTime(timeTooltip, timeZone),
    timeTooltip: timeTooltip || '',
    clientIcon:
      dispatchJob.customer.highlight &&
      customerConstants.highlightIcons[dispatchJob.customer.highlight],
    clientIconTooltip:
      dispatchJob.customer.highlight &&
      customerConstants.highlightTooltips[dispatchJob.customer.highlight],
    clientCode: dispatchJob.customer.code || commonConstants.EM_DASH,
    clientName: dispatchJob.customer.name || commonConstants.EM_DASH,
    from: dispatchJob.stops.at(0)?.suburb || commonConstants.EM_DASH,
    to: dispatchJob.stops.at(-1)?.suburb || commonConstants.EM_DASH,
    service: dispatchJob.service_code || commonConstants.EM_DASH,
    notes: commonConstants.EM_DASH, // TODO note of the current stop?
    isReadyToAllocate: !oneTrackingJobHelpers.checkLockedOrAllocated({
      is_locked: isLocked,
      allocation_state: dispatchJob.allocation_state,
    }),
    originalJob: dispatchJob,
  };
}

export function filterDispatchJobsByDate(
  filteredDispatchJobs: ReadonlyDeep<openapi.DespatchJobsContentOpenapi>,
  date: TZDate,
): ReadonlyDeep<openapi.DespatchJobsContentOpenapi> {
  return filteredDispatchJobs.filter(function (dispatchJob) {
    const actualStop = dispatchJob.stops.find(function (stop) {
      if (!stop.required_at) return false;
      const requiredAtDate = new TZDate(
        stop.required_at,
        stop.required_at.slice(19),
      );
      return isSameDay(requiredAtDate, date, {
        in: tz(dateHelpers.defaultApiTimeZone),
      });
    });
    if (actualStop) return true;
    return false;
  });
}

export function filterDispatchJobsByTab(
  filteredDispatchJobs: ReadonlyDeep<openapi.DespatchJobsContentOpenapi>,
  tab: keyof typeof dispatchJobsTabs,
): ReadonlyDeep<openapi.DespatchJobsContentOpenapi> {
  if (tab === 'preallocated') {
    return filteredDispatchJobs.filter(function (dispatchJob) {
      return dispatchJob.allocation_state === 'preallocated';
    });
  } else if (tab === 'allocated') {
    return filteredDispatchJobs.filter(function (dispatchJob) {
      return dispatchJob.allocation_state === 'allocated';
    });
  } else {
    // tab === 'unallocated'
    return filteredDispatchJobs.filter(function (dispatchJob) {
      // TODO unallocated vs deallocated
      return (
        dispatchJob.allocation_state !== 'preallocated' &&
        dispatchJob.allocation_state !== 'allocated'
      );
    });
  }
}

export function filterDispatchJobsBySearch(
  filteredDispatchJobs: ReadonlyDeep<openapi.DespatchJobsContentOpenapi>,
  search: string,
): ReadonlyDeep<openapi.DespatchJobsContentOpenapi> {
  search = search.toUpperCase();
  return filteredDispatchJobs.filter(function (dispatchJob) {
    return (
      dispatchJob.rockhopper_id.toString().includes(search) ||
      dispatchJob.customer.code.toUpperCase().includes(search) ||
      dispatchJob.customer.name.toUpperCase().includes(search) ||
      dispatchJob.stops.at(0)?.suburb?.toUpperCase().includes(search) ||
      dispatchJob.stops.at(-1)?.suburb?.toUpperCase().includes(search) ||
      dispatchJob.service_code?.toUpperCase().includes(search)
    );
  });
}

function getServiceAreasNums(
  serviceAreas: readonly string[],
  allServiceAreas: ReadonlyDeep<openapi.GetCatalogServiceAreas_200>,
): number[] {
  const selectedServiceAreasNums: number[] = [];
  allServiceAreas.forEach((item) => {
    if (!item.description) return;
    if (serviceAreas.includes(item.description)) {
      selectedServiceAreasNums.push(item.id);
    }
  });
  return selectedServiceAreasNums;
}

// https://kkts.atlassian.net/wiki/spaces/KTD/pages/1217527809/Service+Area+Base+Field+Documentation
// TODO `service_area` from api vs `serv_area` from docs
function getServiceAreaId(servArea: number) {
  return servArea % 100;
}

export function filterDispatchJobsByServiceAreas(
  filteredDispatchJobs: ReadonlyDeep<openapi.DespatchJobsContentOpenapi>,
  serviceAreas: readonly string[],
  allServiceAreas: ReadonlyDeep<openapi.GetCatalogServiceAreas_200>,
): ReadonlyDeep<openapi.DespatchJobsContentOpenapi> {
  // TODO get rid of allServiceAreas and use fixed KMultipleSelectField
  const selectedServiceAreasNums = getServiceAreasNums(
    serviceAreas,
    allServiceAreas,
  );
  return filteredDispatchJobs.filter(function (dispatchJob) {
    return selectedServiceAreasNums.find((serviceArea) => {
      return selectedServiceAreasNums.includes(
        getServiceAreaId(dispatchJob.service_area),
      );
    });
  });
}

export function filterDispatchJobsByServiceGroups(
  filteredDispatchJobs: ReadonlyDeep<openapi.DespatchJobsContentOpenapi>,
  serviceGroups: readonly string[],
): ReadonlyDeep<openapi.DespatchJobsContentOpenapi> {
  return filteredDispatchJobs.filter(function (dispatchJob) {
    if (!dispatchJob.service_groups.length) return false;
    const jobServiceGroups = dispatchJob.service_groups;
    return serviceGroups.find((serviceGroup) => {
      return jobServiceGroups.includes(serviceGroup);
    });
  });
}

export function filterDispatchJobsByMoreFiltersSites(
  filteredDispatchJobs: ReadonlyDeep<openapi.DespatchJobsContentOpenapi>,
  sites: readonly string[],
): ReadonlyDeep<openapi.DespatchJobsContentOpenapi> {
  return filteredDispatchJobs.filter(function (dispatchJob) {
    if (!dispatchJob.site) return false;
    return sites.includes(dispatchJob.site);
  });
}

export function filterDispatchJobsByMoreFiltersGoods(
  filteredDispatchJobs: ReadonlyDeep<openapi.DespatchJobsContentOpenapi>,
  goods: readonly string[],
): ReadonlyDeep<openapi.DespatchJobsContentOpenapi> {
  return filteredDispatchJobs.filter(function (dispatchJob) {
    if (!dispatchJob.goods_codes.length) return false;
    return goods.find((good) => {
      return dispatchJob.goods_codes.includes(good);
    });
  });
}

export function filterDispatchJobsByMoreFiltersSwitches(
  filteredDispatchJobs: ReadonlyDeep<openapi.DespatchJobsContentOpenapi>,
  switches: ReadonlyDeep<{
    isCanSchedule?: boolean;
    isPayOnHold?: boolean;
    isInvoice?: boolean;
  }>,
): ReadonlyDeep<openapi.DespatchJobsContentOpenapi> {
  let _filteredDispatchJobs = filteredDispatchJobs;
  // switches are applied only if they are true
  if (switches.isCanSchedule) {
    _filteredDispatchJobs = filteredDispatchJobs.filter(function (dispatchJob) {
      return dispatchJob.is_schedulable === switches.isCanSchedule;
    });
  }
  if (switches.isPayOnHold) {
    _filteredDispatchJobs = filteredDispatchJobs.filter(function (dispatchJob) {
      return dispatchJob.pay_on_hold === switches.isPayOnHold;
    });
  }
  if (switches.isInvoice) {
    _filteredDispatchJobs = filteredDispatchJobs.filter(function (dispatchJob) {
      return dispatchJob.is_invoiced === switches.isInvoice;
    });
  }
  return _filteredDispatchJobs;
}

export function getTableData(
  dispatchJobs: ReadonlyDeep<openapi.DespatchJobsContentOpenapi> | undefined,
  dispatchJobSearchFormInitialData: ReadonlyDeep<DispatchJobSearchFormInitialData>, // or maybe pass queries here?
  dispatchJobsLocks: ReadonlyDeep<Record<number, boolean>> | undefined,
  tab: keyof typeof dispatchJobsTabs,
  allServiceAreas: ReadonlyDeep<openapi.GetCatalogServiceAreas_200>,
  date: TZDate | undefined,
): DispatchJobsTableDataItem[] {
  const _dispatchJobsLocks = dispatchJobsLocks || {};
  const _dispatchJobsTableData: DispatchJobsTableDataItem[] = [];
  if (!dispatchJobs?.length) return _dispatchJobsTableData;
  if (!dispatchJobSearchFormInitialData.date || !date) {
    return _dispatchJobsTableData;
  }

  let filteredDispatchJobs = _cloneDeep(dispatchJobs);

  filteredDispatchJobs = filterDispatchJobsByDate(filteredDispatchJobs, date);
  filteredDispatchJobs = filterDispatchJobsByTab(filteredDispatchJobs, tab);

  if (dispatchJobSearchFormInitialData.search) {
    filteredDispatchJobs = filterDispatchJobsBySearch(
      filteredDispatchJobs,
      dispatchJobSearchFormInitialData.search,
    );
  }

  if (dispatchJobSearchFormInitialData.serviceAreas?.length) {
    filteredDispatchJobs = filterDispatchJobsByServiceAreas(
      filteredDispatchJobs,
      dispatchJobSearchFormInitialData.serviceAreas,
      allServiceAreas,
    );
  }

  if (dispatchJobSearchFormInitialData.serviceGroups?.length) {
    filteredDispatchJobs = filterDispatchJobsByServiceGroups(
      filteredDispatchJobs,
      dispatchJobSearchFormInitialData.serviceGroups,
    );
  }

  if (dispatchJobSearchFormInitialData.moreFilters.sites?.length) {
    filteredDispatchJobs = filterDispatchJobsByMoreFiltersSites(
      filteredDispatchJobs,
      dispatchJobSearchFormInitialData.moreFilters.sites,
    );
  }

  if (dispatchJobSearchFormInitialData.moreFilters.goods?.length) {
    filteredDispatchJobs = filterDispatchJobsByMoreFiltersGoods(
      filteredDispatchJobs,
      dispatchJobSearchFormInitialData.moreFilters.goods,
    );
  }

  if (dispatchJobSearchFormInitialData.moreFilters.switches) {
    filteredDispatchJobs = filterDispatchJobsByMoreFiltersSwitches(
      filteredDispatchJobs,
      dispatchJobSearchFormInitialData.moreFilters.switches,
    );
  }

  filteredDispatchJobs.forEach(function (dispatchJob) {
    _dispatchJobsTableData.push(getTableItem(dispatchJob, _dispatchJobsLocks));
  });
  return _dispatchJobsTableData;
}
